// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useToast } from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { DEFAULT_SCHEDULE } from '@jane/business-admin/components/shared/schedule/scheduleHelpers';
import { EmbeddedRowModal } from '@jane/business-admin/components/stores/detail/menu/embeddedRowModal/EmbeddedRowModal';
import { MenuCategoryTabs } from '@jane/business-admin/components/stores/detail/menu/landing/MenuCategoryTabs';
import { MenuRowHeader } from '@jane/business-admin/components/stores/detail/menu/landing/MenuRowHeader';
import { MenuRowList } from '@jane/business-admin/components/stores/detail/menu/landing/MenuRowList';
import {
  LearnMoreRecommendedRowsModal,
  MultiThematicRowBanner,
} from '@jane/business-admin/components/stores/detail/menu/landing/multiThematicRows/MultiThematicRowBanner';
import { MenuRowModal } from '@jane/business-admin/components/stores/detail/menu/menuRowModal/MenuRowModal';
import { StoreMenuHeader } from '@jane/business-admin/components/stores/detail/menu/StoreMenuHeader';
import {
  useCreateMenuRow,
  useFetchBloomMenusByStoreId,
  useProductTypes,
  useStoreMenuMetadata,
} from '@jane/business-admin/data-access';
import {
  useModalActionsWithTracking,
  useStoreId,
} from '@jane/business-admin/hooks';
import type { EmbeddedMenuRow, MenuRow } from '@jane/business-admin/types';
import {
  EventNames,
  ModalNames,
  normalizePath,
  track,
} from '@jane/business-admin/util';
import { SDKProvider } from '@jane/dm/internal';

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  ...spacing({ px: 64, pb: 40, pt: 24 }),
});

export const StoreMenu = () => {
  const storeId = useStoreId();
  const { pathname } = useLocation();
  const toast = useToast();

  const [menuRowModalOpen, setMenuRowModalOpen] = useState(false);
  const [embeddedRowModalOpen, setEmbeddedRowModalOpen] = useState(false);
  const [embeddedRow, setEmbeddedRow] = useState<
    EmbeddedMenuRow | null | undefined
  >();
  const [selectedRow, setSelectedRow] = useState<MenuRow | undefined>();
  const [selectedRowHasEmbeddedRow, setSelectedRowHasEmbeddedRow] =
    useState(false);

  const showMultipleThematicRows = useFlag(FLAGS.comm3MultipleThematicRows);

  const { data: allProductTypes, isFetched: productTypesLoaded } =
    useProductTypes();

  const { mutate: createMenuRow } = useCreateMenuRow(storeId);
  const { data: menuMetadata } = useStoreMenuMetadata(storeId);
  const { data: bloomMenus = [], isPending: isLoadingBloomMenus } =
    useFetchBloomMenusByStoreId(storeId);
  const magicRowCount = menuMetadata?.row_count_meta?.magic_row || 0;

  const shouldShowEmbeddedMenuRows = useMemo(() => {
    return bloomMenus.length > 0 && !isLoadingBloomMenus;
  }, [bloomMenus, isLoadingBloomMenus]);

  const handleAddMagicRow = () => {
    createMenuRow(
      {
        row_type: 'magic_row',
        menu_row_type: 'magic_row',
        schedule: {
          ...DEFAULT_SCHEDULE,
          enabled: true,
          use_store_close_time_for_end: undefined,
        },
        ranking: 0,
      },
      {
        onSuccess: () => {
          toast.add({
            label: 'Recommended row added successfully',
            variant: 'success',
          });
        },
        onError: () => {
          toast.add({
            label: 'Failed to add recommended row',
            variant: 'error',
          });
        },
      }
    );
  };

  const openMenuRowModal = (row: MenuRow, hasEmbeddedRow?: boolean) => {
    track({
      event: EventNames.OpenedModal,
      modal_name: ModalNames.EditMenuRow,
      url: normalizePath(pathname, storeId),
    });

    setSelectedRow(row);
    setSelectedRowHasEmbeddedRow(hasEmbeddedRow ?? false);
    setMenuRowModalOpen(true);
  };

  const openEmbeddedRowModal = (
    menuRow: MenuRow,
    row?: EmbeddedMenuRow | null
  ) => {
    track({
      event: EventNames.OpenedModal,
      modal_name: ModalNames.EmbeddedMenuRow,
      url: normalizePath(pathname, storeId),
    });

    setEmbeddedRow(row);

    if (menuRow) {
      setSelectedRow(menuRow);
    }
    setEmbeddedRowModalOpen(true);
  };

  const {
    modalOpen: learnMoreModalOpen,
    openModal: setLearnMoreModalOpen,
    closeModal: setLearnMoreModalClose,
  } = useModalActionsWithTracking(ModalNames.LearnMoreRecommendedRows);

  return (
    <>
      <Wrapper>
        <StoreMenuHeader />
        <MenuCategoryTabs />
        <MenuRowHeader />
        {showMultipleThematicRows && menuMetadata && magicRowCount < 3 && (
          <MultiThematicRowBanner
            setLearnMoreModalOpen={setLearnMoreModalOpen}
            onAddRow={handleAddMagicRow}
            magicRowCount={magicRowCount}
          />
        )}
        <SDKProvider appMode="default">
          <MenuRowList
            onOpenEmbeddedRowModal={openEmbeddedRowModal}
            onOpenMenuRowModal={openMenuRowModal}
            showEmbeddedMenuRows={shouldShowEmbeddedMenuRows}
            storeId={storeId}
          />
        </SDKProvider>
      </Wrapper>
      {menuRowModalOpen && selectedRow && allProductTypes && (
        <MenuRowModal
          row={selectedRow}
          allProductTypes={allProductTypes}
          productTypesLoaded={productTypesLoaded}
          hasEmbeddedMenuRow={selectedRowHasEmbeddedRow}
          closeModal={() => {
            track({
              event: EventNames.ClosedModal,
              modal_name: ModalNames.EditMenuRow,
              url: normalizePath(pathname, storeId),
            });
            setMenuRowModalOpen(false);
          }}
        />
      )}
      {shouldShowEmbeddedMenuRows && embeddedRowModalOpen && (
        <EmbeddedRowModal
          bloomMenus={bloomMenus}
          closeModal={() => {
            track({
              event: EventNames.ClosedModal,
              modal_name: ModalNames.EmbeddedMenuRow,
              url: normalizePath(pathname, storeId),
            });
            setEmbeddedRowModalOpen(false);
          }}
          embeddedMenuRow={embeddedRow}
          menuRow={{
            custom_display_name: selectedRow?.custom_display_name,
            id: selectedRow?.id,
            menu_row_type: selectedRow?.menu_row_type ?? null,
            store_id: selectedRow?.store_id,
          }}
          storeId={Number(storeId)}
        />
      )}
      {showMultipleThematicRows && learnMoreModalOpen && (
        <LearnMoreRecommendedRowsModal
          closeModal={setLearnMoreModalClose}
          open={learnMoreModalOpen}
          onAddRow={handleAddMagicRow}
          magicRowCount={magicRowCount}
        />
      )}
    </>
  );
};
